import {NetWorthRole} from '@transactions/graphql';
import type {NetWorthQuery} from '@transactions/graphql';

/** What amounts render as while the page is hidden. */
export const MASK = '••••••';

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Home-currency cents as whole dollars. Cents are noise at net worth scale,
 * and the investments charts round the same way.
 */
export const formatWholeDollars = (cents: number): string =>
  wholeDollars.format(Math.round(cents / 100));

/** A signed change as "+$1,234" / "−$1,234" (a real minus sign). */
export const formatSignedDollars = (cents: number): string =>
  `${cents < 0 ? '−' : '+'}${formatWholeDollars(Math.abs(cents))}`;

/** A share as a whole percent, with a floor so tiny shares don't read 0%. */
export const formatShare = (share: number): string => {
  if (share > 0 && share < 0.005) return '<1%';
  return `${Math.round(share * 100)}%`;
};

/**
 * Past this, a percentage says more about how little was tracked at the
 * start than about growth ("+1,039,888% since Apr 2010").
 */
const MAX_MEANINGFUL_CHANGE = 10; // 1,000%

/**
 * A change relative to its base, or null when there is no base to compare or
 * the base is too small next to the change for a percentage to mean anything.
 */
export const percentChange = (delta: number, base: number): number | null => {
  if (base === 0) return null;
  const ratio = delta / Math.abs(base);
  return Math.abs(ratio) > MAX_MEANINGFUL_CHANGE ? null : ratio;
};

export const formatPercentChange = (ratio: number): string => {
  const percent = Math.abs(ratio * 100);
  const digits = percent < 10 ? 1 : 0;
  return `${ratio < 0 ? '−' : '+'}${percent.toFixed(digits)}%`;
};

type NetWorthGroupData = NetWorthQuery['netWorth']['groups'][number];

/** How a group reads on the page. */
export type GroupMeta = {
  label: string;
  note: string | null;
  color: string;
  href: string;
};

/**
 * Where a group's accounts live on the accounts page. Page routing, so it
 * lives here; a group missing from it links to the unfiltered list.
 */
const GROUP_ACCOUNT_TYPES: Partial<Record<NetWorthRole, string[]>> = {
  [NetWorthRole.Brokerage]: ['inv:brokerage'],
  [NetWorthRole.Retirement]: ['inv:401k', 'inv:ira', 'inv:roth_ira', 'inv:hsa'],
  [NetWorthRole.Education]: ['inv:education'],
  [NetWorthRole.Cash]: ['dep:checking', 'dep:savings', 'dep:cash'],
  [NetWorthRole.StoredValue]: ['dep:wallet'],
  [NetWorthRole.Spending]: ['dep:credit_card'],
  [NetWorthRole.Loans]: ['dep:loan'],
  [NetWorthRole.SharedLedgers]: ['dep:ledger'],
  [NetWorthRole.Other]: ['dep:other', 'inv:other'],
};

const groupHref = (role: NetWorthRole): string => {
  if (role === NetWorthRole.CompanyStock) return '/equity';
  const types = GROUP_ACCOUNT_TYPES[role];
  return types ? `/accounts?type=${types.join(',')}` : '/accounts';
};

/**
 * The "Desert twilight" palette: dusk indigo and mauve, terracotta and
 * apricot, with a sky blue for contrast — as theme variables (globals.css)
 * so dark mode can re-step the deep ones lighter. Each group keeps its own
 * color — color follows the group, never its position — so a group
 * appearing or being hidden never recolors the rest.
 */
const GROUP_COLORS: Partial<Record<NetWorthRole, string>> = {
  [NetWorthRole.Brokerage]: 'var(--nw-brokerage)',
  [NetWorthRole.Retirement]: 'var(--nw-retirement)',
  [NetWorthRole.Education]: 'var(--nw-education)',
  [NetWorthRole.CompanyStock]: 'var(--nw-company-stock)',
  [NetWorthRole.Cash]: 'var(--nw-cash)',
  [NetWorthRole.StoredValue]: 'var(--nw-stored-value)',
  [NetWorthRole.Spending]: 'var(--nw-spending)',
  [NetWorthRole.Loans]: 'var(--nw-loans)',
  [NetWorthRole.SharedLedgers]: 'var(--nw-shared-ledgers)',
};

/** For a group added on the server before it has a color here. */
const SPARE_COLORS = ['#7d9a7a', '#a86f4c', '#4f6d8f'];

/** "Other" is neutral rather than a color of its own. */
const OTHER_COLOR = 'var(--muted-foreground)';

/**
 * Every group the result lists, keyed by role, with its color. Labels and
 * notes come from the server, which defines the groups.
 */
export const groupMetaOf = (groups: NetWorthGroupData[]): Map<NetWorthRole, GroupMeta> => {
  let spare = 0;
  return new Map(
    groups.map((group) => {
      const color =
        group.role === NetWorthRole.Other
          ? OTHER_COLOR
          : (GROUP_COLORS[group.role] ?? SPARE_COLORS[spare++ % SPARE_COLORS.length]);
      return [
        group.role,
        {
          label: group.label,
          note: group.note ?? null,
          color,
          href: groupHref(group.role),
        },
      ];
    }),
  );
};

/** A group's meta, or a plain fallback for one the result didn't list. */
export const metaFor = (meta: Map<NetWorthRole, GroupMeta>, role: NetWorthRole): GroupMeta =>
  meta.get(role) ?? {
    label: role,
    note: null,
    color: OTHER_COLOR,
    href: groupHref(role),
  };
